import { QueryClient, useQuery, useQueryClient } from 'react-query'
import { UseQueryOptions } from 'react-query/types/react/types'
import axios, { AxiosError } from 'axios'

import * as urls from './urls'

import { parseReporterEventResponse } from '@/queries/reporterEventResponse'
import { ReporterEvent } from '@/types'

export const isReporterEventUnavailable = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  (error.response?.status === 403 || error.response?.status === 404)

export const useWaitingReporterEventsQuery = (
  reporter_id: string | undefined,
) =>
  useQuery<ReporterEvent[]>(
    'waiting_events',
    () =>
      axios
        .get<ReporterEvent[]>(urls.reporterWaitingEvents(reporter_id!))
        .then(r => r.data),
    { enabled: reporter_id !== undefined },
  )

export const fetchReporterEvent = (id: string, reporterId: string) =>
  axios
    .get<unknown>(urls.reporterEvent(reporterId, id))
    .then(parseReporterEventResponse)

export const getReporterEventQueryOptions = (
  client: QueryClient,
  id: string,
  reporter_id: string | undefined,
  options?: Omit<
    UseQueryOptions<ReporterEvent, AxiosError, ReporterEvent>,
    'queryKey' | 'queryFn'
  >,
): UseQueryOptions<ReporterEvent, AxiosError> => {
  const queryKey = ['event', id]
  const error = client.getQueryState<ReporterEvent, AxiosError>(queryKey)?.error

  return {
    ...options,
    queryKey,
    queryFn: () => fetchReporterEvent(id, reporter_id!),
    enabled: reporter_id !== undefined,
    // Stop polling, focus and reconnect refresh after loss of access. Explicit
    // refetch still works, and success clears the cached error to resume refresh.
    ...(isReporterEventUnavailable(error) && {
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }),
  }
}

export const useReporterEventQuery = (
  id: string,
  reporter_id: string | undefined,
  options?: Omit<
    UseQueryOptions<ReporterEvent, AxiosError, ReporterEvent>,
    'queryKey' | 'queryFn'
  >,
) => {
  const client = useQueryClient()
  return useQuery(
    getReporterEventQueryOptions(client, id, reporter_id, options),
  )
}

export const useAvailableReporterEventsQuery = (
  reporter_id: string | undefined,
) =>
  useQuery<ReporterEvent[]>(
    'available_events',
    () =>
      axios
        .get<ReporterEvent[]>(urls.reporterAvailableEvents(reporter_id!))
        .then(r => r.data),
    { enabled: reporter_id !== undefined },
  )
